import { DvToolbarAction, DvToolbarActionEvent } from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import ExcelExportUtils from '@core/utils/excel-export.utils';
import {
  DvDatePeriodPickerElement,
  DvDatePeriods,
  DvDropdownClientSideElement,
  DvDropdownEvent,
  DvFilterRangeKeys,
} from '@devinco/ui/legacy';
import { environment } from '@env/environment';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import { ColDef } from 'ag-grid-community';
import { Subject, filter, take } from 'rxjs';
import { activeRecordFilter } from '@shared/constants/active-record-filter';
import { TripTypeIds } from '@pages/trips-page/constants';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import { EmissionDto } from './interfaces';
import { SSGridExcelExportPayload } from '@features/vt-grid/interfaces/grid-excel-export';

@Injectable()
export class EmissionsGridService {
  private translate = inject(TranslateService);
  private api = inject(RestApiService);
  private toolbarActionsService = inject(ToolbarActionsService);

  excelLoadingTrigger$: Subject<boolean> = new Subject();

  filterPanelElements = [
    new DvDatePeriodPickerElement({
      key: 'Period',
      colspan: 2,
      dateTranslation: this.translate.instant('Report.FilterEdit.Period'),
      filters: DvDatePeriods.filter(
        period => period.filterKey !== DvFilterRangeKeys.All
      ),
    }),
    new DvDropdownClientSideElement({
      colspan: 2,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      placeholder: 'Emission.Filters.DepartmentAll',
      key: 'Department',
      appendTo: 'body',
      isMultipleSelectionEnabled: true,
      valueKey: 'DepartmentID',
      labelKeys: ['DepartmentDescription'],
      open: (params: DvDropdownEvent) => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      colspan: 2,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'Driver',
      appendTo: 'body',
      placeholder: 'Emissions.Filters.DriverAll',
      isMultipleSelectionEnabled: true,
      labelKeys: ['UserNameDescription'],
      valueKey: 'UserID',
      ready: params => {
        const request: GetRowsRequest = {
          entity: Entity.User,
          fields: ['UserID', 'UserNameDescription'],
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 2,
      closeOnSelect: false,
      key: 'VehicleTag',
      isMultipleSelectionEnabled: true,
      isCheckboxEnabled: true,
      valueKey: 'TagDescription',
      labelKeys: ['TagDescription'],
      placeholder: 'Emissions.Filters.TagsAll',
      ready: params => {
        const request: GetRowsRequest = {
          entity: 'TagVehicle',
          fields: ['TagDescription'],
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 2,
      key: 'TripType',
      valueKey: 'TripTypeID',
      labelKeys: ['TripType'],
      placeholder: 'Emissions.Filters.TripTypesAll',
      ready: ({ api }) => {
        api.setItems(
          Object.keys(TripTypeIds).map(key => ({
            TripTypeID: Number(key),
            TripType: this.translate.instant(
              `TripType.${TripTypeIds[Number(key) as keyof typeof TripTypeIds]}`
            ),
          }))
        );
      },
    }),
  ];

  getColDefs = (): ColDef<EmissionDto>[] => [
    {
      field: 'TrackerObjectID',
      headerName: 'Grid.Id',
      type: [ColumnType.Hidden],
      initialHide: true,
      enableRowGroup: false,
    },
    {
      field: 'RegistrationNumber',
      headerName: 'EmissionsGrid.RegistrationNumber',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      field: 'VehicleDescription',
      headerName: 'EmissionsGrid.VehicleDescription',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      field: 'Username',
      headerName: 'EmissionsGrid.Username',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      field: 'UserDescription',
      headerName: 'EmissionsGrid.UserDescription',
      type: [ColumnType.Text],
      enableRowGroup: false,
      hide: true,
    },
    {
      field: 'DepartmentDescription',
      headerName: 'EmissionsGrid.DepartmentDescription',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      field: 'DistanceInPeriod',
      headerName: 'EmissionsGrid.DistanceInPeriod',
      type: [ColumnType.Km],
      enableRowGroup: false,
    },
    {
      field: 'CO2Emission',
      headerName: 'EmissionsGrid.CO2Emission',
      type: [ColumnType.Number],
      enableRowGroup: false,
    },
    {
      field: 'CO2InPeriod',
      headerName: 'EmissionsGrid.CO2InPeriod',
      type: [ColumnType.NumberDecimal],
      enableRowGroup: false,
    },
    {
      field: 'FuelType',
      headerName: 'EmissionsGrid.FuelType',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      type: ColumnType.TripType,
      field: 'TripTypeID',
      filter: 'agNumberColumnFilter',
      hide: true,
    },
    {
      field: 'DeletedVehicle',
      type: [ColumnType.Checkbox],
      headerName: 'EmissionsGrid.DeletedVehicle',
      hide: true,
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key =
            data.value === true
              ? 'EmissionsGrid.DeletedVehicle'
              : 'EmissionsGrid.AvailableVehicle';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
      enableRowGroup: false,
    },
    {
      field: 'Odometer',
      type: [ColumnType.Km],
      headerName: 'EmissionsGrid.Odometer',
    },
    {
      field: 'TripDurationHM',
      type: [ColumnType.TimeText],
      headerName: 'EmissionsGrid.TripDurationHM',
    },
    {
      field: 'TripsInPeriod',
      type: [ColumnType.Number],
      headerName: 'EmissionsGrid.TripsInPeriod',
    },
    {
      field: 'VehicleTag',
      headerName: 'EmissionsGrid.VehicleTag',
      type: [ColumnType.Text],
      filterParams: {
        maxNumConditions: 100,
      },
      suppressColumnsToolPanel: true,
      hide: true,
    },
    {
      field: 'DepartmentID',
      type: [ColumnType.Number],
      headerName: 'EmissionsGrid.DepartmentId',
      filterParams: {
        maxNumConditions: 100,
      },
      suppressColumnsToolPanel: true,
      hide: true,
    },
    {
      field: 'UserID',
      headerName: 'EmissionsGrid.UserId',
      type: [ColumnType.Number],
      filterParams: {
        maxNumConditions: 100,
      },
      suppressColumnsToolPanel: true,
      hide: true,
    },
    {
      field: 'StartTime',
      headerName: 'EmissionsGrid.StartTime',
      type: [ColumnType.DateTime],
      suppressColumnsToolPanel: true,
      hide: true,
    },
    {
      field: 'KWhPer10km',
      headerName: 'EmissionsGrid.KWhPer10km',
      type: [ColumnType.NumberDecimal],
    },
    {
      field: 'KWhInPeriod',
      headerName: 'EmissionsGrid.KWhInPeriod',
      type: [ColumnType.NumberDecimal],
    },
    {
      field: 'KWhLastYear',
      headerName: 'EmissionsGrid.KWhLastYear',
      type: [ColumnType.NumberDecimal],
    },
  ];

  getGridActions(
    emissionFiltersFn: () => GetRowsRequest,
    emissionFileNameFn: () => string
  ): DvToolbarAction[] {
    const rootUrl = `${environment.endpoints.restApi}/report/download`;

    return [
      {
        ...vtPresetActions.excel,
        type: 'raised',
        buttonTextKey: 'EmissionsGrid.Report',
        menuItems: [
          {
            labelKey: 'EmissionsGrid.EmissionsLog',
            clickFn: (params: DvToolbarActionEvent) =>
              this.toolbarActionsService.serverSideExcelExport(
                Entity.Emission,
                params.api,
                params.context,
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('EmissionsGrid.EmissionsLog')
                )}${emissionFileNameFn()}`
              ),
          },
          {
            labelKey: 'EmissionsGrid.EmissionsPrPurpose',
            clickFn: () => {
              this.exportEmissionsExcel(
                `${rootUrl}/EmissionPrPurpose`,
                emissionFiltersFn(),
                `${ExcelExportUtils.getCapitalizedFileName(
                  this.translate.instant('EmissionsGrid.EmissionsPrPurpose')
                )}${emissionFileNameFn()}`
              );
            },
          },
        ],
      },
      {},
      vtPresetActions.reset,
      vtPresetActions.fit,
      vtPresetActions.reload,
    ];
  }

  private exportEmissionsExcel(
    url: string,
    payload: SSGridExcelExportPayload,
    fileName?: string
  ): void {
    this.excelLoadingTrigger$.next(true);
    this.toolbarActionsService
      .downloadExcel(url, payload, Entity.Emission, fileName)
      .pipe(
        filter(value => !!value),
        take(1)
      )
      .subscribe(_ => this.excelLoadingTrigger$.next(false));
  }
}
